// M4-B §7: shale finance — reserve-based lending (borrowing base = PV10 of PDP at the bank deck × advance rate,
// redetermined 1 April / 1 October; a falling deck shrinks the line → 6 months to repay the deficiency or Chapter 11),
// the hedge covenant (from 2009: ≥ 50% of next-12-month PDP hedged or the base is cut 20%), M4 hedges (swaps, zero-cost
// collars, puts, three-ways; settled monthly against the calendar-month WTI average), cost of capital
// (BASE_RATE_M4 + energy high-yield spread + ESG bps, via finance.loanRate M4-B hunk) and capital discipline (2020+:
// capex / operating cash flow above the reinvestment norm costs reputation each quarter).
import { SHALE_FINANCE, BASE_RATE_M4, lerpAt, modHypRate, M4_FROM, NGL_RATIO, MCF_PER_BOE, MMBTU_PER_MCF, henryHub } from '../../data/m4/rules.js';
import { dateToDay, dayToDate } from '../../core/time.js';
import { allWells } from '../indexes.js';
import { yf } from '../defs.js';
import { spotPrice } from '../../economy/market.js';
import { netback, opexPerWell } from '../production.js';
import { hedgedBbl, hedgeCapacityBbl, putValue, DAYS_PER_MONTH } from '../m3/hedging.js';
import { clamp, notify, spend, earn, book, fmt$ } from '../util.js';
import { mk, peek, m4On, nid, r2 } from './marketCore.js';
import { wtiToday, fieldPriceInfo } from './marketPrices.js';
import { contangoPerMonth } from './marketNegative.js';
import { esgLoanBps, wellSplitsGas } from './marketGas.js';

export const RBL_ID = 'RBL';
export const RBL_MARGIN = 2.25;           // % over the base rate at low utilization (est.)
export const RBL_UTIL_PREMIUM = 1.0;      // extra % when > 75% drawn (est.)
export const DEFICIENCY_DAYS = 182;       // six months to cure a borrowing-base deficiency
export const PDP_MONTHS = 180;            // 15-year PDP forecast
export const PDP_DISCOUNT = 0.10;         // PV10
export const ECON_LIMIT_BBLD = 0.5;
export const HEDGE_UNWIND_SPREAD = 0.02;
export const CH11 = { cashLoss: 0.5, oilLoss: 0.5, rep: 40 }; // DESIGN §6.4: Chapter 11 = −50% assets, rep −40, continue
// Backwardation ($/bbl/month) when the curve is not in contango (est.), for swap strips.
export const BACKWARDATION_M4 = [[2000, 0.3], [2003, 0.5], [2005, 0], [2008.5, 1.0], [2008.8, 0], [2011.5, 0.5], [2013, 0.6], [2014.5, 0.8], [2014.8, 0],
  [2017.6, 0], [2018.8, 0.4], [2019.5, 0.2], [2020.2, 0], [2021.3, 0.6], [2022.4, 2.5], [2023, 0.6], [2025, 0.3]];

// ---- rates ---------------------------------------------------------------------------------------------------
export const baseRateM4 = (y) => lerpAt(BASE_RATE_M4, y);
export const hySpreadBps = (y) => lerpAt(SHALE_FINANCE.energyHySpreadBps, y);
// finance.loanRate hook (M4-B): % for new bank loans from 2000.
export function m4LoanRate(state) {
  const y = yf(state);
  const repMult = clamp(1.4 - 0.8 * clamp(state.company.reputation, 0, 100) / 100, 0.6, 1.4);
  return Math.max(1, baseRateM4(y) + hySpreadBps(y) / 100 * repMult + esgLoanBps(state) / 100);
}
export function rblRatePct(state) {
  const R = peek(state)?.rbl;
  const loan = rblLoan(state);
  const util = R?.line > 0 ? (loan?.principal ?? 0) / R.line : 0;
  return r2(baseRateM4(yf(state)) + RBL_MARGIN + RBL_UTIL_PREMIUM * clamp((util - 0.75) / 0.25, 0, 1) + esgLoanBps(state) / 100);
}

// ---- forward curve for hedges (WTI, $/bbl) --------------------------------------------------------------------
export function curveUsdPerMonth(state) {
  const y = yf(state);
  const c = contangoPerMonth(y);
  const S = Math.max(1, Math.abs(wtiToday(state)));
  if (c > 0) return Math.min(c, 0.12 * S);
  return -Math.min(lerpAt(BACKWARDATION_M4, y), 0.06 * S);
}
export function forwardM4(state, k) {
  const S = wtiToday(state);
  const c = curveUsdPerMonth(state);
  return Math.max(0.5, S + c * (1 - Math.pow(0.8, k)) / 0.2);
}

// ---- PDP forecast ---------------------------------------------------------------------------------------------
function wellCurve(p) {
  const di = p.di ?? 0.5, b = p.b ?? 0.5, qi = p.qi ?? 0;
  if (p.dmin) return (t) => modHypRate(qi, di, b, p.dmin, t);
  if (b <= 1e-6) return (t) => qi * Math.exp(-di * t);
  return (t) => qi / Math.pow(1 + b * di * t, 1 / b);
}
// Split wells (workstream A) carry gas & NGL in their boe: value them in oil-equivalent barrels of revenue.
export function oilValueFactor(state, w) {
  if (!wellSplitsGas(w)) return 1;
  const st = w.prod.streams ?? {};
  const y = yf(state);
  const wti = Math.max(1, wtiToday(state));
  return clamp((st.oil ?? 1) + (st.ngl ?? 0) * lerpAt(NGL_RATIO, y) + (st.gas ?? 0) * MCF_PER_BOE * MMBTU_PER_MCF * henryHub(y) / wti, 0, 1);
}
// Monthly PDP volumes (oil-value bbl) for the next `months`, by field.
export function pdpForecast(state, months = PDP_MONTHS) {
  const byField = {};
  const total = new Array(months).fill(0);
  for (const w of allWells(state)) {
    if (!w.prod || w.ownerOperatorId || !['PRODUCING', 'SHUT_IN'].includes(w.status)) continue;
    const p = w.prod;
    const curve = wellCurve(p);
    const a = (p.age ?? 0) / 365;
    const base = curve(a);
    if (!(base > 0)) continue;
    const q0 = p.rate > 0 ? p.rate : base * (p.lastShare ?? 1);
    const scale = q0 / base * oilValueFactor(state, w);
    const arr = (byField[w.fieldId] ??= new Array(months).fill(0));
    for (let m = 0; m < months; m++) {
      const q = curve(a + (m + 0.5) / 12) * scale;
      if (q < ECON_LIMIT_BBLD) break;
      const v = q * DAYS_PER_MONTH;
      arr[m] += v; total[m] += v;
    }
  }
  return { byField, total };
}

// Per-bbl margin of a field at a deck price (the field's realized structure scaled to the deck).
function fieldMarginAtDeck(state, fieldId, deckFrac, wells, ratebbld) {
  const nb = netback(state, fieldId);
  const info = fieldPriceInfo(state, fieldId);
  const quality = info.price > 0 ? nb.gross / info.price : 1; // sour/API, depth of market
  const deckGross = Math.max(0, info.price) * deckFrac * quality;
  const rr = nb.gross > 0 ? (nb.royalty + nb.severance) / nb.gross : 0.2;
  const opex = ratebbld > 0 ? opexPerWell(yf(state)) * wells / ratebbld : 0;
  return deckGross * (1 - rr) - nb.transport - nb.region - opex;
}

// Hedge book settlement values per month at a deck (WTI) price, months 0..11 (M3 positions use the Brent-like spot deck).
function hedgeValueByMonth(state, deckWti, deckSpot) {
  const out = new Array(12).fill(0);
  const idx = (due) => clamp(Math.floor((due - state.day) / DAYS_PER_MONTH), 0, 11);
  for (const p of state.hedging?.positions ?? []) {
    if (p.kind === 'forward') for (const l of p.months) { if (!l.settled && l.due - state.day < 366) out[idx(l.due)] += (l.price - deckSpot) * p.bblPerMonth; }
    else if (p.kind === 'future' && p.side !== 'long' && p.expiryDay - state.day < 366) out[idx(p.expiryDay)] += (p.price - deckSpot) * p.bbl;
    else if (p.kind === 'put' && p.expiryDay - state.day < 366) out[idx(p.expiryDay)] += Math.max(0, p.strike - deckSpot) * p.bbl;
  }
  for (const h of peek(state)?.hedges ?? []) for (const l of h.legs) if (!l.settled && l.due - state.day < 366) out[idx(l.due)] += legPayoff(h, deckWti) * h.bblPerMonth;
  return out;
}

export function borrowingBase(state) {
  const y = yf(state);
  const deckFrac = 1 - SHALE_FINANCE.bankDeckDiscount;
  const { byField, total } = pdpForecast(state);
  let pv = 0;
  const fieldPv = {};
  for (const [fid, arr] of Object.entries(byField)) {
    const ws = allWells(state).filter((w) => w.fieldId === fid && w.prod && !w.ownerOperatorId && w.status === 'PRODUCING');
    const rate = ws.reduce((a, w) => a + (w.prod.rate ?? 0), 0);
    const margin = fieldMarginAtDeck(state, fid, deckFrac, ws.length, rate);
    let v = 0;
    for (let m = 0; m < arr.length; m++) if (arr[m] > 0 && margin > 0) v += arr[m] * margin / Math.pow(1 + PDP_DISCOUNT, (m + 0.5) / 12);
    fieldPv[fid] = Math.round(v); pv += v;
  }
  const deckWti = Math.max(0, wtiToday(state)) * deckFrac;
  const deckSpot = Math.max(0, spotPrice(state)) * deckFrac;
  const hv = hedgeValueByMonth(state, deckWti, deckSpot);
  let hedgePv = 0;
  for (let m = 0; m < 12; m++) hedgePv += hv[m] / Math.pow(1 + PDP_DISCOUNT, (m + 0.5) / 12);
  const next12 = total.slice(0, 12).reduce((a, b) => a + b, 0);
  const hedged = hedgedNext12(state);
  const share = next12 > 0 ? Math.min(1, hedged / next12) : 0;
  const covenantOn = y >= SHALE_FINANCE.hedgeCovenant.from;
  const covenantCut = covenantOn && next12 > 0 && share < SHALE_FINANCE.hedgeCovenant.minShareNext12;
  let base = Math.max(0, (pv + hedgePv) * SHALE_FINANCE.rblAdvanceRate);
  if (covenantCut) base *= 0.8;
  return { base: Math.round(base), pdpPv10: Math.round(pv), hedgePv: Math.round(hedgePv), fieldPv, deckWti: r2(deckWti), deckSpot: r2(deckSpot), pdpNext12Bbl: Math.round(next12),
    hedgedNext12Bbl: Math.round(hedged), hedgedShare: r2(share), covenantOn, covenantCut, advanceRate: SHALE_FINANCE.rblAdvanceRate };
}
export function hedgedNext12(state) {
  let b = hedgedBbl(state);
  for (const h of peek(state)?.hedges ?? []) for (const l of h.legs) if (!l.settled && l.due - state.day < 366) b += h.bblPerMonth;
  return b;
}

// ---- the RBL ---------------------------------------------------------------------------------------------------
export const rblLoan = (state) => state.company.loans.find((l) => l.id === RBL_ID) ?? null;
export const rblDrawn = (state) => rblLoan(state)?.principal ?? 0;

function setDrawn(state, amount) {
  let loan = rblLoan(state);
  if (amount <= 0.5) { if (loan) state.company.loans = state.company.loans.filter((l) => l !== loan); return; }
  if (!loan) { loan = { id: RBL_ID, principal: 0, ratePct: rblRatePct(state), startDay: state.day, kind: 'rbl' }; state.company.loans.push(loan); }
  loan.principal = Math.round(amount);
}

// Setup helper (Quick Start 2014, tests): an RBL with a commitment and an opening draw. `ratePct` optional.
export function setupRbl(state, { commitment, drawn = 0, ratePct = null } = {}) {
  const M = mk(state);
  const bb = borrowingBase(state);
  M.rbl = { commitment: Math.round(commitment), base: bb.base, line: Math.round(Math.min(commitment, Math.max(bb.base, drawn))), openedDay: state.day, lastRedet: state.day, deficiency: null, history: [], chapter11: 0 };
  setDrawn(state, drawn);
  const loan = rblLoan(state);
  if (loan && ratePct != null) loan.ratePct = ratePct;
  return M.rbl;
}

export function openRblImpl(state, { commitment } = {}) {
  if (!m4On(state)) return { ok: false, msg: 'Reserve-based lending arrives with the shale era (2000+).' };
  if (peek(state)?.rbl) return { ok: false, msg: 'You already have a reserve-based loan.' };
  const bb = borrowingBase(state);
  if (!(bb.base > 0)) return { ok: false, msg: 'The banks lend against proved developed producing reserves: bring wells on production first.' };
  const c = Math.round(Number(commitment) || bb.base);
  setupRbl(state, { commitment: Math.max(1, c), drawn: 0 });
  const R = mk(state).rbl;
  R.line = Math.min(R.commitment, bb.base);
  return { ok: true, msg: `Reserve-based facility signed: ${fmt$(R.commitment)} commitment, borrowing base ${fmt$(bb.base)} (PV10 ${fmt$(bb.pdpPv10)} × ${SHALE_FINANCE.rblAdvanceRate}). Redetermined each April and October.` };
}

export function drawRblImpl(state, amount) {
  const R = peek(state)?.rbl;
  if (!R) return { ok: false, msg: 'No reserve-based facility. Open one first.' };
  if (R.deficiency) return { ok: false, msg: 'The banks have frozen the line until the borrowing-base deficiency is cured.' };
  const amt = Math.round(Number(amount) || 0);
  if (!(amt > 0)) return { ok: false, msg: 'Enter an amount.' };
  const avail = Math.max(0, R.line - rblDrawn(state));
  if (amt > avail + 0.5) return { ok: false, msg: `Only ${fmt$(avail)} is available under the borrowing base.` };
  setDrawn(state, rblDrawn(state) + amt);
  state.company.cash += amt;
  return { ok: true, msg: `Drew ${fmt$(amt)} on the RBL (${fmt$(rblDrawn(state))} of ${fmt$(R.line)} drawn).` };
}

export function repayRblImpl(state, amount) {
  const R = peek(state)?.rbl;
  const drawn = rblDrawn(state);
  if (!R || !(drawn > 0)) return { ok: false, msg: 'Nothing drawn on the RBL.' };
  const amt = Math.min(Math.round(Number(amount ?? drawn) || drawn), drawn, Math.floor(Math.max(0, state.company.cash)));
  if (!(amt > 0)) return { ok: false, msg: 'Not enough cash.' };
  state.company.cash -= amt;
  setDrawn(state, drawn - amt);
  if (R.deficiency && rblDrawn(state) <= R.line + 0.5) { R.deficiency = null; notify(state, 'news', { kind: 'finance', text: 'Borrowing-base deficiency cured. The banks breathe again.' }); }
  return { ok: true, msg: `Repaid ${fmt$(amt)} on the RBL.` };
}

export function redetermine(state, reason = 'scheduled') {
  const R = peek(state)?.rbl;
  if (!R) return null;
  const bb = borrowingBase(state);
  const prevLine = R.line;
  R.base = bb.base;
  R.line = Math.min(R.commitment, bb.base);
  R.lastRedet = state.day;
  const drawn = rblDrawn(state);
  const cut = prevLine > 0 ? 1 - R.line / prevLine : 0;
  const rec = { day: state.day, reason, base: bb.base, line: R.line, prevLine, cut: r2(cut), deckWti: bb.deckWti, hedgedShare: bb.hedgedShare, covenantCut: bb.covenantCut, drawn };
  R.history.push(rec);
  if (R.history.length > 40) R.history.shift();
  if (drawn > R.line + 0.5) {
    const prev = R.deficiency; // one still outstanding keeps its due date: a fresh redetermination doesn't restart the clock
    R.deficiency = { amount: Math.round(drawn - R.line), dueDay: prev?.dueDay ?? state.day + DEFICIENCY_DAYS, sinceDay: prev?.sinceDay ?? state.day };
    notify(state, 'news', { kind: 'finance', level: 'major', pause: true,
      text: `Borrowing-base redetermination: the banks ${cut > 0.005 ? `cut your line ${Math.round(cut * 100)}% to` : 'hold your line at'} ${fmt$(R.line)}${bb.covenantCut ? ' (hedge covenant breached: −20%)' : ''}. You owe ${fmt$(R.deficiency.amount)} back within six months or face Chapter 11.` });
  } else {
    R.deficiency = null;
    notify(state, 'news', { kind: 'finance', level: cut > 0.1 ? 'warn' : 'info', text: `Borrowing base redetermined at ${fmt$(bb.base)}; line ${fmt$(R.line)} (${fmt$(drawn)} drawn).${bb.covenantCut ? ' Hedge covenant breached: −20%.' : ''}` });
  }
  return rec;
}

function chapter11(state) {
  const R = peek(state).rbl;
  const c = state.company;
  const before = rblDrawn(state);
  if (c.cash > 0) c.cash *= 1 - CH11.cashLoss;
  for (const f of Object.values(state.fields)) if (f.storage?.bbl > 0) f.storage.bbl *= 1 - CH11.oilLoss;
  setDrawn(state, R.line);
  R.commitment = Math.max(R.line, 1);
  R.deficiency = null;
  R.chapter11 = (R.chapter11 ?? 0) + 1;
  c.reputation = Math.max(0, c.reputation - CH11.rep);
  c.stats.chapter11 = (c.stats.chapter11 ?? 0) + 1;
  notify(state, 'news', { kind: 'finance', level: 'major', pause: true,
    text: `Chapter 11. The lenders convert ${fmt$(before - R.line)} of debt into the company; you keep running it, poorer and humbled (reputation −${CH11.rep}).` });
}

export function tickRbl(state, { dom, month }) {
  const R = peek(state)?.rbl;
  if (!R) return;
  const loan = rblLoan(state);
  if (dom === 1 && loan) loan.ratePct = rblRatePct(state);
  if (R.deficiency) { // settled first: a deficiency falling due on a redetermination day is swept, not replaced
    if (rblDrawn(state) <= R.line + 0.5) R.deficiency = null;
    else if (state.day >= R.deficiency.dueDay) {
      const owe = rblDrawn(state) - R.line;
      if (state.company.cash >= owe) { state.company.cash -= owe; setDrawn(state, R.line); R.deficiency = null; notify(state, 'news', { kind: 'finance', level: 'warn', text: `The banks swept ${fmt$(owe)} from your accounts to cure the deficiency.` }); }
      else chapter11(state);
    }
  }
  if (dom === 1 && SHALE_FINANCE.redeterminationMonths.includes(month) && R.lastRedet !== state.day) redetermine(state);
}

// ---- M4 hedges ---------------------------------------------------------------------------------------------------
export const HEDGE_KINDS = ['swap', 'collar', 'put', 'three_way'];
function legPayoff(h, S) {
  switch (h.kind) {
    case 'swap': return h.strike - S;
    case 'put': return Math.max(0, h.floor - S);
    case 'collar': return Math.max(0, h.floor - S) - Math.max(0, S - h.ceiling);
    case 'three_way': return Math.max(0, h.floor - S) - Math.max(0, h.subFloor - S) - Math.max(0, S - h.ceiling);
    default: return 0;
  }
}
function legDueDays(state, months) {
  const { year, month } = dayToDate(state.day);
  return Array.from({ length: months }, (_, i) => dateToDay(year, month + i + 1, 1));
}
// Strike X with put(F, X, T) = target (bisection; Bachelier put is increasing in X).
function solvePutStrike(F, T, target) {
  let lo = 0, hi = 2 * F;
  for (let i = 0; i < 50; i++) { const mid = (lo + hi) / 2; if (putValue(F, mid, T) < target) lo = mid; else hi = mid; }
  return (lo + hi) / 2;
}

export function quoteHedgeM4(state, { kind = 'swap', months = 12, floor = null, subFloor = null } = {}) {
  const m = clamp(Math.round(Number(months) || 12), 1, 24);
  const fwd = Array.from({ length: m }, (_, i) => forwardM4(state, i + 1));
  const F = fwd.reduce((a, b) => a + b, 0) / m;
  const T = (m + 1) / 2 / 12;
  const q = { kind, months: m, wti: r2(wtiToday(state)), strip: r2(F), forwards: fwd.map(r2), available: m4On(state) && HEDGE_KINDS.includes(kind), reason: null };
  if (!m4On(state)) q.reason = 'M4 hedges (swaps, collars, three-ways) open in 2000.';
  if (kind === 'swap') q.strike = r2(F);
  if (kind === 'put') { q.floor = r2(floor ?? 0.9 * F); q.premium = r2(putValue(F, q.floor, T)); q.premiumFrac = r2(q.premium / Math.max(1e-6, F)); }
  if (kind === 'collar') { q.floor = r2(floor ?? 0.9 * F); q.ceiling = r2(2 * F - q.floor); q.premium = 0; }
  if (kind === 'three_way') {
    q.floor = r2(floor ?? 0.9 * F); q.subFloor = r2(subFloor ?? 0.75 * F);
    const net = Math.max(0, putValue(F, q.floor, T) - putValue(F, q.subFloor, T));
    q.ceiling = r2(2 * F - solvePutStrike(F, T, net)); q.premium = 0;
  }
  return q;
}

// `strike` (swap) and `skipCap` are for scenario setup (Quick Start 2014: 50% swapped at $95), not the UI.
export function hedgeM4Impl(state, { kind = 'swap', months = 12, bblPerMonth, floor = null, subFloor = null, strike = null, skipCap = false } = {}) {
  const q = quoteHedgeM4(state, { kind, months, floor, subFloor });
  if (kind === 'swap' && strike != null) q.strike = r2(strike);
  if (!q.available) return { ok: false, msg: q.reason ?? `Unknown hedge kind ${kind}.` };
  const v = Math.round(Number(bblPerMonth) || 0);
  if (!(v > 0)) return { ok: false, msg: 'Enter barrels per month.' };
  const cap = hedgeCapacityBbl(state);
  const already = hedgedBbl(state) + (peek(state)?.hedges ?? []).reduce((a, h) => a + h.legs.filter((l) => !l.settled).length * h.bblPerMonth, 0);
  if (!skipCap && already + v * q.months > cap + 1e-6) return { ok: false, msg: `You may hedge at most a year of production (${Math.round(cap).toLocaleString('en-US')} bbl; ${Math.round(already).toLocaleString('en-US')} already hedged).` };
  const premium = (q.premium ?? 0) * v * q.months;
  if (premium > 0) {
    if (state.company.cash < premium) return { ok: false, msg: `The put premium is ${fmt$(premium)}.` };
    state.company.cash -= premium; book(state, 'hedging', -premium); // hedging is an income line: premiums are negative income
  }
  const M = mk(state);
  const h = { id: nid(state, 'hm'), kind, bblPerMonth: v, months: q.months, strike: q.strike ?? null, floor: q.floor ?? null, ceiling: q.ceiling ?? null, subFloor: q.subFloor ?? null,
    premium: q.premium ?? 0, openDay: state.day, legs: legDueDays(state, q.months).map((due) => ({ due, settled: false, price: null, value: null })), pnl: -premium };
  M.hedges.push(h);
  const terms = kind === 'swap' ? `at ${fmt$(h.strike)}` : kind === 'put' ? `floor ${fmt$(h.floor)} for ${fmt$(h.premium)}/bbl` : kind === 'collar' ? `${fmt$(h.floor)}–${fmt$(h.ceiling)} (zero cost)` : `${fmt$(h.subFloor)}/${fmt$(h.floor)}/${fmt$(h.ceiling)} (zero cost)`;
  return { ok: true, msg: `${kind.replace('_', '-')} on ${v.toLocaleString('en-US')} bbl/month of WTI for ${q.months} months ${terms}.`, hedgeId: h.id };
}

// Mark-to-market of an M4 hedge at today's curve.
export function hedgeMtm(state, h) {
  let v = 0;
  h.legs.forEach((l) => {
    if (l.settled) return;
    const k = Math.max(0, (l.due - state.day) / DAYS_PER_MONTH);
    const F = forwardM4(state, Math.max(1, Math.round(k)));
    const T = k / 12;
    let x;
    if (h.kind === 'swap') x = h.strike - F;
    else if (h.kind === 'put') x = putValue(F, h.floor, T);
    else if (h.kind === 'collar') x = putValue(F, h.floor, T) - putValue(F, 2 * F - h.ceiling, T);
    else x = putValue(F, h.floor, T) - putValue(F, h.subFloor, T) - putValue(F, 2 * F - h.ceiling, T);
    v += x * h.bblPerMonth;
  });
  return v;
}

export function closeHedgeM4Impl(state, id) {
  const M = peek(state);
  const h = (M?.hedges ?? []).find((x) => x.id === id);
  if (!h) return { ok: false, msg: 'No such hedge.' };
  const mtm = hedgeMtm(state, h);
  const v = mtm - Math.abs(mtm) * HEDGE_UNWIND_SPREAD - HEDGE_UNWIND_SPREAD * Math.abs(wtiToday(state)) * h.bblPerMonth * h.legs.filter((l) => !l.settled).length * 0.1;
  state.company.cash += v; book(state, 'hedging', v);
  h.pnl += v;
  if (v > 0) state.company.stats.hedgeGainsM4 = (state.company.stats.hedgeGainsM4 ?? 0) + v;
  M.hedges = M.hedges.filter((x) => x !== h);
  return { ok: true, msg: `Hedge ${h.id} unwound: ${v >= 0 ? 'you receive' : 'you pay'} ${fmt$(Math.abs(v))}.` };
}

// Daily: track the calendar-month WTI average; on the 1st settle legs due against last month's average.
export function tickWtiAverage(state) {
  const M = mk(state);
  const { year, month } = dayToDate(state.day);
  const key = `${year}-${month}`;
  const W = M.wti;
  if (W.key !== key) { if (W.n > 0) { W.lastAvg = r2(W.sum / W.n); W.lastKey = W.key; } W.key = key; W.sum = 0; W.n = 0; }
  W.sum += wtiToday(state); W.n += 1;
}
export function tickHedgesM4(state) {
  const M = peek(state);
  if (!M?.hedges?.length) return;
  const S = M.wti?.lastAvg ?? wtiToday(state);
  for (const h of [...M.hedges]) {
    for (const l of h.legs) {
      if (l.settled || state.day < l.due) continue;
      const v = legPayoff(h, S) * h.bblPerMonth;
      l.settled = true; l.price = S; l.value = Math.round(v);
      if (v >= 0) earn(state, v, 'hedging'); else { state.company.cash += v; book(state, 'hedging', v); }
      h.pnl += v;
      if (v > 0) state.company.stats.hedgeGainsM4 = (state.company.stats.hedgeGainsM4 ?? 0) + v;
    }
    if (h.legs.every((l) => l.settled)) {
      M.hedges = M.hedges.filter((x) => x !== h);
      notify(state, 'news', { kind: 'finance', text: `Your ${h.kind.replace('_', '-')}${(h.strike ?? h.floor) != null ? ` at $${Number(h.strike ?? h.floor).toFixed(2)}` : ''} has run off: ${h.pnl >= 0 ? 'gain' : 'loss'} ${fmt$(Math.abs(h.pnl))}.` });
    }
  }
}
// M3 hedge gains after 2000 count toward hedgeGainsM4 too (the achievement doesn't care which desk booked them).
export function tickM3HedgeGains(state) {
  const H = state.hedging;
  if (!H) return;
  const M = mk(state);
  const prev = M.m3Realized ?? H.realized ?? 0;
  const d = (H.realized ?? 0) - prev;
  if (d > 0) state.company.stats.hedgeGainsM4 = (state.company.stats.hedgeGainsM4 ?? 0) + d;
  M.m3Realized = H.realized ?? 0;
}

// ---- capital discipline (2020+) ------------------------------------------------------------------------------------
export function reinvestment(state, months = 3) {
  const hist = state.finance.history.slice(-months);
  let capex = 0, cfo = 0;
  for (const m of hist) {
    const c = (m.capex ?? 0) + (m.leases ?? 0) + (m.charter ?? 0);
    capex += c;
    cfo += (m.net ?? 0) + c;
  }
  return { capex: Math.round(capex), cfo: Math.round(cfo), ratio: cfo > 0 ? r2(capex / cfo) : (capex > 0 ? 99 : 0), norm: lerpAt(SHALE_FINANCE.reinvestmentNorm, yf(state)) };
}
export function tickCapitalDiscipline(state, { dom, month }) {
  if (dom !== 1 || ![1, 4, 7, 10].includes(month) || yf(state) < 2020) return;
  const hasWells = allWells(state).some((w) => w.status === 'PRODUCING' && !w.ownerOperatorId);
  if (!hasWells) return;
  const r = reinvestment(state);
  if (!(r.capex > 0) && !(r.cfo > 0)) return;
  const M = mk(state);
  if (r.ratio > r.norm) {
    state.company.reputation = Math.max(0, state.company.reputation - 1);
    M.discipline = { day: state.day, ...r, verdict: 'over' };
    notify(state, 'news', { kind: 'finance', level: 'warn', text: `Shareholders want capital discipline: you reinvested ${r.ratio < 99 ? Math.round(r.ratio * 100) + '%' : 'more than all'} of operating cash flow (the market rewards ≤ ${Math.round(r.norm * 100)}%). Reputation −1.` });
  } else {
    state.company.reputation = Math.min(100, state.company.reputation + 0.5);
    M.discipline = { day: state.day, ...r, verdict: 'disciplined' };
  }
}

// ---- view ---------------------------------------------------------------------------------------------------------
export function shaleFinanceViewImpl(state) {
  const y = yf(state);
  const on = y >= M4_FROM;
  const M = peek(state);
  const R = M?.rbl ?? null;
  const bb = on ? borrowingBase(state) : null;
  const { month } = dayToDate(state.day);
  const nextRedetMonth = SHALE_FINANCE.redeterminationMonths.find((mm) => mm > month) ?? SHALE_FINANCE.redeterminationMonths[0];
  const { year } = dayToDate(state.day);
  const nextRedetDay = dateToDay(nextRedetMonth > month ? year : year + 1, nextRedetMonth, 1);
  return {
    year: y, active: on,
    rates: on ? { base: r2(baseRateM4(y)), hySpreadBps: Math.round(hySpreadBps(y)), esgBps: Math.round(esgLoanBps(state)), loanRate: r2(m4LoanRate(state)), rblRate: R ? rblRatePct(state) : r2(baseRateM4(y) + RBL_MARGIN) } : null,
    rbl: R ? { ...R, history: R.history.map((h) => ({ ...h })), drawn: rblDrawn(state), available: Math.max(0, R.line - rblDrawn(state)), utilization: R.line > 0 ? r2(rblDrawn(state) / R.line) : 0, nextRedetDay,
      deficiency: R.deficiency ? { ...R.deficiency, daysLeft: R.deficiency.dueDay - state.day } : null } : null,
    borrowingBase: bb, nextRedetDay,
    covenant: { ...SHALE_FINANCE.hedgeCovenant, active: y >= SHALE_FINANCE.hedgeCovenant.from, hedgedShare: bb?.hedgedShare ?? 0 },
    hedges: (M?.hedges ?? []).map((h) => ({ ...h, legs: h.legs.map((l) => ({ ...l })), mtm: Math.round(hedgeMtm(state, h)), legsLeft: h.legs.filter((l) => !l.settled).length })),
    quotes: on ? Object.fromEntries(HEDGE_KINDS.map((k) => [k, quoteHedgeM4(state, { kind: k, months: 12 })])) : null,
    hedgeCapacityBbl: Math.round(hedgeCapacityBbl(state)), hedgeGainsM4: Math.round(state.company.stats.hedgeGainsM4 ?? 0),
    wtiMonthAvg: M?.wti?.lastAvg ?? null,
    discipline: y >= 2020 ? { ...reinvestment(state), last: M?.discipline ?? null } : null,
  };
}
